export async function testServer(iceServer: RTCIceServer): Promise<TestResult> {
    const urls = Array.isArray(iceServer.urls) ? iceServer.urls[0] : iceServer.urls;
    const logs: string[] = [];
    const log = (msg: string) => logs.push(`[${new Date().toLocaleTimeString()}] ${msg}`);

    return new Promise<TestResult>(async resolve => {
        let pc: RTCPeerConnection | null = null;
        let candidates: RTCIceCandidate[] = [];
        let timer: any;
        let isFinished = false;
        let stream: MediaStream | null = null;

        const finish = (result: FinalResult) => {
            if (isFinished) return;
            isFinished = true;

            clearTimeout(timer);
            const isError = result !== "Успешно";
            
            if (isError) {
                console.group(`%c[ICE Test FAILED] ${urls}`, 'color: red; font-weight: bold;');
                logs.forEach(l => console.warn(l));
                if (candidates.length > 0) {
                    console.info("Candidates gathered:", candidates);
                } else {
                    console.error("No candidates were gathered at all.");
                }
                console.groupEnd();
            } else {
                console.info(`%c[ICE Test OK] ${urls}`, 'color: green; font-weight: bold;');
            }

            if (pc) {
                pc.close();
                pc = null;
            }

            if (stream) {
                stream.getTracks().forEach(t => t.stop());
                stream = null;
            }

            resolve({
                isError,
                msg: result,
                context: isError ? { logs, urls } : undefined,
            });
        };

        timer = setTimeout(() => {
            log("TIMEOUT: Gathering took more than 10 seconds.");
            // Перед тем как упасть по таймауту, проверим, может мы уже нашли что-то полезное?
            const types = candidates.map(c => c.type);
            if (urls.match(/^stuns?:/i) && types.includes("srflx")) {
                finish("Успешно");
            } else if (urls.match(/^turns?:/i) && types.includes("relay")) {
                finish("Успешно");
            } else {
                finish("Недоступен");
            }
        }, 10000);

        try {
            pc = new RTCPeerConnection({
                iceServers: [iceServer],
                iceTransportPolicy: (iceServer.urls as string[]).find(url => url.match(/^turns?:/i)) ? "relay" : "all",
            });

            log(`Initialized PeerConnection for ${urls}`);

            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach(track => pc!.addTrack(track, stream!));
                log("Added audio track to trigger ICE gathering on Safari");
            } catch (e) {
                log("Could not get audio track, continuing without media");
            }

            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    const c = event.candidate;
                    log(`Candidate found: type=${c.type}, protocol=${c.protocol}, addr=${c.address}:${c.port}`);
                    candidates.push(c);

                    // Ускоренное завершение: если нашли то, что доказывает работу сервера - выходим успешно сразу
                    if (urls.match(/^stuns?:/i) && c.type === "srflx") {
                        log("SUCCESS: Found srflx candidate. STUN is working.");
                        finish("Успешно");
                    } else if (urls.match(/^turns?:/i) && c.type === "relay") {
                        log("SUCCESS: Found relay candidate. TURN is working.");
                        finish("Успешно");
                    }
                }
            };

            pc.onicegatheringstatechange = () => {
                log(`Gathering state changed to: ${pc?.iceGatheringState}`);
                if (pc?.iceGatheringState === "complete") {
                    log("Gathering state is 'complete'. Evaluating candidates...");
                    
                    const types = candidates.map(c => c.type);
                    if (urls.match(/^turns?:/i)) {
                        finish(types.includes("relay") ? "Успешно" : "Недоступен");
                    } else if (urls.match(/^stuns?:/i)) {
                        finish(types.includes("srflx") ? "Успешно" : "Недоступен");
                    } else {
                        finish(candidates.length > 0 ? "Успешно" : "Недоступен");
                    }
                }
            };

            pc.onicecandidateerror = (e: any) => {
                log(`ICE Error Event: code=${e.errorCode}, text=${e.errorText}, url=${e.url}`);
            };

            const offer = await pc.createOffer({ offerToReceiveAudio: true });
            await pc.setLocalDescription(offer);
            log("Offer set, gathering started");

        } catch (err: any) {
            log(`FATAL ERROR: ${err.message || err}`);
            finish("Недоступен");
        }
    });
}

export type FinalResult = "Успешно" | "Ошибка авторизации" | "Недоступен";

export interface TestResult {
    msg: string;
    isError: boolean;
    context?: any;
}

function extractTransportPolicy(iceServer: RTCIceServer): RTCIceTransportPolicy {
    return (iceServer.urls as string[]).find(url => url.match(/^turns?:/i))
        ? "relay"
        : "all";
}
